<script setup>
import FormSection from "../../../Shared/Forms/FormSection";
import ActionMessage from "../../../Shared/Forms/ActionMessage";
import Button from "../../../Shared/Forms/Button";
import { useForm } from "@inertiajs/inertia-vue3";
import { ref } from "vue";
import zxcvbn from 'zxcvbn/dist/zxcvbn';

const showPasswordField = ref(true);
const passwordScore = ref(0);
const form = useForm({
  current_password: null,
  password: null,
  password_confirmation: null,
  confirm_unsecure_password: null,
})

function checkStrength() {
  if(!form.password) return passwordScore.value = 0;
  passwordScore.value = zxcvbn(form.password).score + 1;
}

function submitForm() {
  if (form.password.length < 8) {
    form.setError('password', 'Uw wachtwoord moet minimaal 8 tekens bevatten.');
    return;
  }
  if (passwordScore.value <=2 && !form.confirm_unsecure_password) {
    form.setError('confirm_unsecure_password', 'U moet het gebruik van een onveilig wachtwoord eerst bevestigen.');
    return;
  }
  form.post(route('password.reset'))
}


const props = defineProps(['profile'])

function updatePassword() {
  if (form.password.length < 8) {
    form.setError('password', 'Uw wachtwoord moet minimaal 8 tekens bevatten.');
    return;
  }
  if (passwordScore.value <=2 && !form.confirm_unsecure_password) {
    form.setError('confirm_unsecure_password', 'U moet het gebruik van een onveilig wachtwoord eerst bevestigen.');
    return;
  }

  form.put(route('customer-password.update'), {
    errorBag: 'updatePassword',
    preserveScroll: true,
  });

}

</script>
<template>
    <FormSection @submitted="updatePassword">
        <template #title>
          Update Wachtwoord
        </template>

        <template #description>
          Zorg ervoor dat u een lang en willekeurig wachtwoord gebruikt om zo uw account veilig te houden.
        </template>

        <template #form>

          <div class="col-span-6 sm:col-span-4">
            <label for="current_password" class="block text-sm font-medium text-gray-700"> Huidig Wachtwoord </label>
            <div class="mt-1">
              <input v-model="form.current_password" id="current_password" name="current_password" type="password" autocomplete="password" required="" placeholder="Bevestig Wachtwoord" class="appearance-none block w-full px-3 py-2 border border-primary rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm" />
              <div v-if="form.errors.current_password" v-text="form.errors.current_password" class="text-red-500 text-xs mt-1"></div>
            </div>
          </div>

          <div class="col-span-6 sm:col-span-4">

            <label for="do-not-autofill" class="block text-sm font-medium text-gray-700"> Nieuw wachtwoord </label>
            <div class="mt-1 relative">
              <input id="do-not-autofill" name="do-not-autofill" :type="showPasswordField?'password':'text'" autocomplete="do-not-autofill" required=""
                     v-model="form.password" @input="checkStrength()"
                     class="appearance-none block w-full px-3 py-2 border border-primary rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm">
              <button
                class="block w-7 h-7 text-center text-xl leading-0 absolute top-1 right-2 text-gray-400 focus:outline-none hover:text-primary transition-colors"
                @click.prevent="showPasswordField=!showPasswordField">
                <!-- Eye -->
                <svg v-if="showPasswordField" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                <!-- Eye Off -->
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                </svg>
              </button>
              <div class="flex mt-2">
                <template v-for="(v,i) in 5">
                  <div class="w-1/5 px-1">
                    <div class="h-2 rounded-xl transition-colors"
                         :class="i<passwordScore?(passwordScore<=2?'bg-red-400':(passwordScore<=4?'bg-yellow-400':'bg-green-500')):'bg-gray-200'"></div>
                  </div>
                </template>
              </div>
              <div v-if="form.errors.password" v-text="form.errors.password" class="text-red-500 text-xs mt-1"></div>
            </div>
          </div>

          <div class="col-span-6 sm:col-span-4">
            <label for="confirm-password" class="block text-sm font-medium text-gray-700"> Bevestig Wachtwoord </label>
            <div class="mt-1">
              <input v-model="form.password_confirmation" id="confirm-password" name="confirm-password" type="password" autocomplete="do-not-autofill" required="" placeholder="Bevestig Wachtwoord" class="appearance-none block w-full px-3 py-2 border border-primary rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm" />
              <div v-if="form.errors.password_confirmation" v-text="errors.password_confirmation" class="text-red-500 text-xs mt-1"></div>
            </div>

            <div v-if="passwordScore<=2 && form.password && form.password === form.password_confirmation" class="mt-2 flex space-x-2">
              <div class="flex items-center h-5">
                <input v-model="form.confirm_unsecure_password" id="confirm_unsecure_password" name="confirm_unsecure_password" type="checkbox" class="h-4 w-4 border-gray-300 rounded text-primary focus:ring-primary" />
              </div>
              <label for="confirm_unsecure_password" class="text-sm text-gray-500">Uw wachtwoord is zeer onveilig. Wilt u toch doorgaan?</label>
            </div>

            <div v-if="form.errors.confirm_unsecure_password" v-text="form.errors.confirm_unsecure_password" class="text-red-500 text-xs mt-1"></div>
          </div>

        </template>

        <template #actions>
            <ActionMessage :on="form.recentlySuccessful" class="mr-3">
                {{ __('Opgeslagen.') }}
            </ActionMessage>

            <Button :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                {{ __('Opslaan') }}
            </Button>
        </template>
    </FormSection>
</template>
